import { Controller } from 'stimulus';
import { Chart } from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import gsap from 'gsap';

Chart.register(annotationPlugin);

export default class extends Controller {
  static targets = ["chart", "leftArrow", "rightArrow", "spinner", "lockSvgTemplate", "unlockSvgTemplate"]
  static values = {
    data: Array,
    timeframe: String
  }

  connect() {
    this.itemsPerPage = 6
    this.currentIndex = 0
    this.isNavigating = false
    this.isFirstRender = true

    const computedStyle = getComputedStyle(document.documentElement)
    this.gray300Color = computedStyle.getPropertyValue('--gray-300-hex').trim()
    this.gray500Color = computedStyle.getPropertyValue('--gray-500-hex').trim()
    this.gray600Color = computedStyle.getPropertyValue('--gray-600-hex').trim()

    // Find the page with the next unlock
    const nextUnlockIndex = this.findNextUnlockIndex()
    if (nextUnlockIndex > 0) {
      // Calculate which page this index should be on
      this.currentIndex = Math.floor(nextUnlockIndex / (this.itemsPerPage - 1)) * (this.itemsPerPage - 1)
    }

    // Ensure canvas is hidden initially
    this.chartTarget.style.display = 'none'
    this.initializeChart()
    this.updateArrowVisibility()
  }

  findNextUnlockIndex() {
    return this.dataValue.findIndex(item => item.locked)
  }

  initializeChart() {
    const ctx = this.chartTarget.getContext('2d')

    if (this.chart) {
      this.chart.destroy()
    }

    const visibleData = this.getVisibleData()

    const unlockedGradient = ctx.createLinearGradient(0, 0, 0, 400)
    unlockedGradient.addColorStop(0, '#17a9ff')
    unlockedGradient.addColorStop(0.9, 'rgba(12, 85, 128, 0.6)')
    unlockedGradient.addColorStop(1, 'rgba(12, 85, 128, 0.1)')

    const lockedGradient = ctx.createLinearGradient(0, 0, 0, 400)
    lockedGradient.addColorStop(0, '#1C2A2D')
    lockedGradient.addColorStop(0.8, 'rgba(28, 42, 45, 0.5)')
    lockedGradient.addColorStop(1, 'rgba(20, 29, 31, 0)')

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: visibleData.labels,
        datasets: [{
          label: null,
          data: visibleData.data,
          borderColor: this.getLineColors(visibleData.locked),
          stepped: 'after',
          tension: 1,
          pointRadius: 0,
          pointBackgroundColor: this.getLineColors(visibleData.locked),
          fill: true,
          segment: {
            borderColor: ctx => {
              const visibleIndex = ctx.p0DataIndex;
              const currentLocked = visibleData.locked[visibleIndex];
              const nextLocked = visibleData.locked[visibleIndex + 1];

              // Handle edge cases
              if (typeof currentLocked === 'undefined' || typeof nextLocked === 'undefined') {
                return this.gray500Color;
              }

              // If both points are locked, use gray
              if (currentLocked && nextLocked) {
                return this.gray500Color;
              }
              // If both points are unlocked, use bright blue
              if (!currentLocked && !nextLocked) {
                return '#3B82F6';
              }
              // If transitioning from unlocked to locked, use dark blue
              if (!currentLocked && nextLocked) {
                return '#0C5580';
              }
              // Default to gray
              return this.gray500Color;
            },
            backgroundColor: ctx => {
              const visibleIndex = ctx.p0DataIndex;
              const currentLocked = visibleData.locked[visibleIndex];
              const nextLocked = visibleData.locked[visibleIndex + 1];

              // Handle edge case
              if (typeof currentLocked === 'undefined') {
                return lockedGradient;
              }

              // If transitioning from unlocked to locked, use the next point's status
              if (!currentLocked && nextLocked) {
                return lockedGradient;
              }

              // Otherwise use current point's status
              return currentLocked ? lockedGradient : unlockedGradient;
            }
          }
        }]
      },
      options: {
        animations: false,
        hover: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: false
          },
          annotation: {
            annotations: {
              currentDate: {
                type: 'line',
                scaleID: 'x',
                display: (ctx) => {
                  const today = new Date();
                  const labels = ctx.chart.data.labels;
                  const visibleDates = labels.map(label => {
                    const [month, day, year] = label.split(' ');
                    return new Date(`${month} ${day} 20${year}`);
                  });

                  // Only show if today falls within the visible date range
                  return today >= visibleDates[0] && today <= visibleDates[visibleDates.length - 1];
                },
                value: (ctx) => {
                  const today = new Date();
                  const labels = ctx.chart.data.labels;
                  const visibleDates = labels.map(label => {
                    const [month, day, year] = label.split(' ');
                    return new Date(`${month} ${day} 20${year}`);
                  });

                  // Find the position between the dates where today falls
                  for (let i = 0; i < visibleDates.length - 1; i++) {
                    if (today >= visibleDates[i] && today <= visibleDates[i + 1]) {
                      // Calculate the fraction between the two points
                      const totalDays = (visibleDates[i + 1] - visibleDates[i]) / (1000 * 60 * 60 * 24);
                      const daysSinceStart = (today - visibleDates[i]) / (1000 * 60 * 60 * 24);
                      const fraction = daysSinceStart / totalDays;

                      // Return interpolated position
                      return i + fraction;
                    }
                  }

                  return 0; // Default position if not found (though line will be hidden)
                },
                borderColor: this.gray300Color,
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                  display: true,
                  content: 'Today',
                  position: 'top',
                  backgroundColor: this.gray600Color,
                  color: this.gray300Color,
                  font: {
                    family: "'FTPolar', sans-serif"
                  },
                  padding: 6
                }
              }
            }
          },
          tooltip: {
            enabled: false,
            external: function(context) {
              let tooltipEl = document.getElementById('chartjs-tooltip');
              if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'chartjs-tooltip';
                tooltipEl.style.opacity = 0;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.transition = 'opacity 0.3s ease, left 0.3s ease, top 0.3s ease';
                document.body.appendChild(tooltipEl);
              }

              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }

              const dataPoint = tooltipModel.dataPoints[0];
              if (dataPoint.dataIndex === 0 && this.currentIndex === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }

              const index = this.currentIndex + dataPoint.dataIndex - 1;
              let batchData;

              if (dataPoint.dataIndex === 0) {
                batchData = this.dataValue[this.currentIndex - 1];
              } else {
                batchData = this.dataValue[index];
              }

              const unlockedPercent = this.calculateTotalVestedAtIndex(index).toFixed(1);
              const lockedPercent = (100 - unlockedPercent).toFixed(1);

              const formattedDate = this.formatDate(batchData.date);

              const svgTemplate = batchData.locked ?
                this.lockSvgTemplateTarget.innerHTML :
                this.unlockSvgTemplateTarget.innerHTML;

              const innerHtml = `
                <div class="flex flex-col bg-gray-800 px-3 py-2 rounded-xl">
                  <div class="flex items-center gap-2 mb-1 sm:mb-0">
                    <div class="inline-flex items-center">${svgTemplate}</div>
                    <div class="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                      <span class="text-white">${batchData.batchName}</span>
                      <span class="text-gray-300 text-xs">${formattedDate}</span>
                    </div>
                  </div>
                  <span class="text-sky-400">${unlockedPercent}% unlocked</span>
                  <span class="text-white">${lockedPercent}% locked</span>
                </div>
              `;

              const chartPosition = context.chart.canvas.getBoundingClientRect();
              tooltipEl.style.pointerEvents = 'none';
              tooltipEl.innerHTML = innerHtml;

              const tooltipX = chartPosition.left + window.scrollX + tooltipModel.caretX;
              const tooltipY = chartPosition.top + window.scrollY + tooltipModel.caretY - 60;

              tooltipEl.style.transform = 'translate(-50%, -100%)';
              tooltipEl.style.left = tooltipX + 'px';
              tooltipEl.style.top = tooltipY + 'px';
              tooltipEl.style.opacity = 1;
            }.bind(this)
          },
          interaction: {
            mode: 'index',
            intersect: false
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            grid: {
              color: this.gray600Color,
              drawBorder: false,
              drawTicks: false
            },
            ticks: {
              padding: 16,
              align: 'start',
              callback: function(value) {
                if (value === 0) return ''
                return `${value}%`
              },
              color: this.gray300Color,
              font: {
                family: "'FTPolar', sans-serif"
              }
            }
          },
          x: {
            grid: {
              color: this.gray600Color,
              drawBorder: false,
              drawTicks: false
            },
            ticks: {
              padding: 16,
              color: this.gray300Color,
              font: {
                family: "'FTPolar', sans-serif"
              }
            }
          }
        }
      }
    })

    if(this.isFirstRender){
      this.isFirstRender = false
      // Animate spinner out and chart in
      gsap.to(this.spinnerTarget, {
        opacity: 0,
        duration: 0.3,
        onComplete: () => {
          this.spinnerTarget.style.display = 'none'
          this.chartTarget.style.display = 'block'
          gsap.fromTo(this.chartTarget,
            { opacity: 0 },
            { opacity: 1, duration: 0.3 }
          )
        }
      })
    } else {
      // For subsequent renders (pagination), ensure the chart is visible
      this.chartTarget.style.display = 'block'
    }
  }

  getVisibleData() {
    const schedule = this.dataValue
    const startIdx = this.currentIndex
    const endIdx = Math.min(startIdx + (this.itemsPerPage - 1), schedule.length)

    // Calculate the cumulative total up to the start index (excluding it)
    const previousTotal = startIdx > 0 ?
      this.calculateTotalVestedAtIndex(startIdx - 1) : 0

    // Get the date of the previous item (if it exists) to create our initial point
    let initialDate;
    if (startIdx > 0) {
      // Use the previous item's date directly
      initialDate = new Date(schedule[startIdx - 1].date)
    } else {
      // For the first page, go back by an appropriate increment from the first date
      initialDate = new Date(schedule[startIdx].date)

      // Determine the time increment by analyzing data pattern
      // If dates are about a month apart, go back a month
      // If dates are closer together, use a smaller increment
      if (schedule.length > 1) {
        const firstDate = new Date(schedule[0].date)
        const secondDate = new Date(schedule[1].date)
        const daysDiff = (secondDate - firstDate) / (1000 * 60 * 60 * 24)

        if (daysDiff >= 25) { // Monthly or longer
          initialDate.setMonth(initialDate.getMonth() - 1)
        } else if (daysDiff >= 12) { // Bi-weekly
          initialDate.setDate(initialDate.getDate() - 14)
        } else { // Weekly or shorter
          initialDate.setDate(initialDate.getDate() - 7)
        }
      } else {
        // Default to monthly if we only have one data point
        initialDate.setMonth(initialDate.getMonth() - 1)
      }
    }

    // Get the locked status of the previous point (if it exists), or false for the very first point
    const previousLocked = startIdx > 0 ? schedule[startIdx - 1].locked : false

    return {
      labels: [
        this.formatDate(initialDate.toISOString()),
        ...schedule.slice(startIdx, endIdx).map(item => this.formatDate(item.date))
      ],
      data: [
        previousTotal,
        ...schedule.slice(startIdx, endIdx).map((_, index) =>
          this.calculateTotalVestedAtIndex(startIdx + index)
        )
      ],
      locked: [
        previousLocked,
        ...schedule.slice(startIdx, endIdx).map(item => item.locked)
      ]
    }
  }

  calculateTotalVestedAtIndex(index) {
    if (index < 0) return 0
    return this.dataValue
      .slice(0, index + 1)
      .reduce((sum, item) => sum + item.percentage, 0)
  }

  formatDate(date) {
    const dateObj = new Date(date)
    return dateObj.toLocaleString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
  }

  getLineColors(lockedArray) {
    const nextUnlockGlobalIndex = this.findNextUnlockIndex()

    return lockedArray.map((locked, visibleIndex) => {
      // Convert visible index to global index
      const globalIndex = this.currentIndex + visibleIndex - 1 // -1 because first item is previous point

      if (globalIndex < nextUnlockGlobalIndex) {
        return '#3B82F6' // Blue for unlocked
      } else if (globalIndex === nextUnlockGlobalIndex) {
        return '#0C5580' // Special color for next unlock
      } else {
        return this.gray500Color // Gray for remaining locked items
      }
    })
  }

  getSegmentColor(ctx, lockedArray) {
    const visibleIndex = ctx.p0DataIndex
    const globalIndex = this.currentIndex + visibleIndex - 1 // -1 because first item is previous point
    const nextUnlockGlobalIndex = this.findNextUnlockIndex()

    if (globalIndex < nextUnlockGlobalIndex) {
      return '#3B82F6' // Blue for unlocked
    } else if (globalIndex === nextUnlockGlobalIndex) {
      return '#0C5580' // Special color for next unlock
    } else {
      return this.gray500Color // Gray for remaining locked items
    }
  }

  navigateLeft() {
    if (this.currentIndex > 0 && !this.isNavigating) {
      this.isNavigating = true
      this.updateArrowVisibility()

      gsap.to(this.chartTarget, {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          this.currentIndex = Math.max(0, this.currentIndex - (this.itemsPerPage - 1))
          this.initializeChart()
          gsap.to(this.chartTarget, {
            opacity: 1,
            duration: 0.2,
            onComplete: () => {
              this.isNavigating = false
              this.updateArrowVisibility()
            }
          })
        }
      })
    }
  }

  navigateRight() {
    const remainingItems = this.dataValue.length - this.currentIndex

    if (remainingItems > (this.itemsPerPage - 1) && !this.isNavigating) {
      this.isNavigating = true
      this.updateArrowVisibility()

      gsap.to(this.chartTarget, {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          this.currentIndex = this.currentIndex + (this.itemsPerPage - 1)
          this.initializeChart()
          gsap.to(this.chartTarget, {
            opacity: 1,
            duration: 0.2,
            onComplete: () => {
              this.isNavigating = false
              this.updateArrowVisibility()
            }
          })
        }
      })
    }
  }

  updateArrowVisibility() {
    // Disable left arrow if at start or currently navigating
    this.leftArrowTarget.disabled = this.currentIndex === 0 || this.isNavigating

    // Disable right arrow if no more items or currently navigating
    const remainingItems = this.dataValue.length - this.currentIndex
    this.rightArrowTarget.disabled = remainingItems <= (this.itemsPerPage - 1) || this.isNavigating
  }
}
